// Full logout flow, shared by the app shell (and any future logout trigger).
//
// Order matters: hit the server logout FIRST (the access token is still present,
// so authApi authenticates the call), then drop the local session, then — if
// the session was a UAE Pass login — redirect the browser to the UAE Pass
// front-channel single-logout URL to end the IdP session. UAE Pass returns to
// the seller logout redirect URI (configured server-side). Non-UAE-Pass or
// failed logouts fall back to a client-side clear + route to /login.
import { useRouter } from 'vue-router'
import { useQueryClient } from '@tanstack/vue-query'
import { logger } from '@core/logging/logger'
import { useAuthStore } from '../store/authStore'
import { useLogoutMutation } from '../api/mutations/useLogoutMutation'
import { authPaths } from '../authRoute'

export function useLogout() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const authStore = useAuthStore()
  const { mutate, isPending } = useLogoutMutation()

  function clearLocalSession(): void {
    authStore.clearSession()
    queryClient.clear()
  }

  function goToLogin(): void {
    router.push(authPaths.LOGIN)
  }

  function logout(): void {
    const refreshToken = authStore.refreshToken
    // No refresh token to invalidate — just clear locally and bounce to login.
    if (!refreshToken) {
      clearLocalSession()
      goToLogin()
      return
    }

    mutate(
      { refreshToken },
      {
        onSuccess: ({ uaePassLogoutUrl }) => {
          clearLocalSession()
          if (uaePassLogoutUrl) {
            window.location.href = uaePassLogoutUrl
            return
          }
          goToLogin()
        },
        onError: (error) => {
          // Server logout failed — still drop the local session so the user is
          // logged out client-side; the server session lapses on its TTL.
          logger.error('Logout request failed', error)
          clearLocalSession()
          goToLogin()
        },
      },
    )
  }

  return { logout, isLoggingOut: isPending }
}
